import React, { useState } from 'react';
import { MessageSquarePlus, Camera, X, Send, Loader } from 'lucide-react';
import html2canvas from 'html2canvas';
import { api } from '../../services/api';
import Swal from 'sweetalert2';
import { toast } from 'sonner';

export const FeedbackWidget = ({ user }) => {
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    category: 'General Feedback',
    description: '',
    severity: 'Medium',
    screenshot: null,
    screenshotPreview: null,
  });
  const [capturing, setCapturing] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleOpenForm = () => {
    setShowForm(true);
  };

  const handleCloseForm = () => {
    // Check if there's unsaved data
    if (formData.description || formData.screenshot) {
      Swal.fire({
        icon: 'warning',
        title: 'Discard feedback?',
        text: 'You have unsaved changes. Are you sure you want to close?',
        showCancelButton: true,
        confirmButtonColor: '#dc2626',
        cancelButtonColor: '#6b7280',
        confirmButtonText: 'Yes, discard',
        cancelButtonText: 'Keep editing',
      }).then(result => {
        if (result.isConfirmed) {
          resetForm();
          setShowForm(false);
        }
      });
    } else {
      setShowForm(false);
    }
  };

  const resetForm = () => {
    setFormData({
      category: 'General Feedback',
      description: '',
      severity: 'Medium',
      screenshot: null,
      screenshotPreview: null,
    });
  };

  const captureScreenshot = async () => {
    try {
      setCapturing(true);

      // Hide the feedback modal temporarily
      const modal = document.getElementById('feedback-modal');
      if (modal) {
        modal.style.display = 'none';
      }

      // Wait a moment for the modal to disappear
      await new Promise(resolve => setTimeout(resolve, 100));

      // Capture the screenshot
      const canvas = await html2canvas(document.body, {
        useCORS: true,
        scale: 0.5, // Reduce quality for smaller file size
        width: window.innerWidth,
        height: window.innerHeight,
        windowWidth: window.innerWidth,
        windowHeight: window.innerHeight,
      });

      // Restore the modal
      if (modal) {
        modal.style.display = 'flex';
      }

      // Convert canvas to blob
      canvas.toBlob(
        blob => {
          if (blob) {
            // Create preview URL
            const previewUrl = URL.createObjectURL(blob);

            // Create file from blob
            const file = new File([blob], 'screenshot.jpg', { type: 'image/jpeg' });

            setFormData(prev => ({
              ...prev,
              screenshot: file,
              screenshotPreview: previewUrl,
            }));

            toast.success('Screenshot captured!', {
              duration: 1500,
            });
          }
        },
        'image/jpeg',
        0.7
      );
    } catch (error) {
      console.error('Screenshot capture error:', error);
      toast.error('Could not capture screenshot. You can still submit feedback without it.');
    } finally {
      setCapturing(false);
    }
  };

  const removeScreenshot = () => {
    if (formData.screenshotPreview) {
      URL.revokeObjectURL(formData.screenshotPreview);
    }
    setFormData(prev => ({
      ...prev,
      screenshot: null,
      screenshotPreview: null,
    }));
  };

  const getCurrentPageName = () => {
    // Try to find the main heading on the page (H1 first, then H2)
    const h1Elements = document.querySelectorAll('h1');

    // Check for H1, but skip the app header "Qualiz"
    for (const h1 of h1Elements) {
      const text = h1.textContent.trim();
      if (text && h1.offsetParent !== null) {
        // Skip if it's just "Qualiz" (the app header)
        if (text.toLowerCase() === 'qualiz') {
          continue;
        }
        // offsetParent !== null means the element is visible
        return text;
      }
    }

    // If no meaningful H1 found, check for H2
    const h2Elements = document.querySelectorAll('h2');
    for (const h2 of h2Elements) {
      const text = h2.textContent.trim();
      if (text && h2.offsetParent !== null) {
        return text;
      }
    }

    // Fallback to document title or URL
    return document.title || window.location.href;
  };

  const handleSubmit = async e => {
    e.preventDefault();

    if (!formData.description.trim()) {
      toast.error('Please provide a description of your feedback');
      return;
    }

    try {
      setSubmitting(true);

      // Prepare feedback data
      const feedbackData = {
        category: formData.category,
        description: formData.description.trim(),
        severity: formData.category === 'Bug Report' ? formData.severity : undefined,
        pageUrl: getCurrentPageName(),
        userRole: user.role,
        browserInfo: {
          userAgent: navigator.userAgent,
          viewport: {
            width: window.innerWidth,
            height: window.innerHeight,
          },
        },
      };

      // Add screenshot if available
      if (formData.screenshot) {
        feedbackData.screenshot = formData.screenshot;
      }

      // Submit feedback
      const result = await api.createFeedback(feedbackData);

      if (result.success) {
        toast.success('Thank you for your feedback. We will review it soon.', {
          duration: 3000,
        });

        resetForm();
        setShowForm(false);
      } else {
        throw new Error(result.message || 'Failed to submit feedback');
      }
    } catch (error) {
      console.error('Feedback submission error:', error);
      toast.error(error.message || 'Could not submit feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Floating Button */}
      <button
        onClick={handleOpenForm}
        className="fixed bottom-6 right-6 z-40 bg-blue-600 hover:bg-blue-700 text-white rounded-full p-4 shadow-lg transition-all duration-200 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        title="Send Feedback"
        data-testid="send-feedback-button"
      >
        <MessageSquarePlus className="w-6 h-6" />
      </button>

      {/* Feedback Form Modal */}
      {showForm && (
        <div
          id="feedback-modal"
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          onClick={e => {
            if (e.target.id === 'feedback-modal') {
              handleCloseForm();
            }
          }}
        >
          <div
            className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-blue-600 text-white px-6 py-4 rounded-t-lg flex justify-between items-center">
              <div className="flex items-center gap-2">
                <MessageSquarePlus className="w-6 h-6" />
                <h2 className="text-xl font-semibold">Send Feedback</h2>
              </div>
              <button
                onClick={handleCloseForm}
                className="text-white hover:bg-blue-700 rounded-full p-1 transition-colors"
                disabled={submitting}
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Thank You Message */}
            <div className="px-6 pt-4 pb-2">
              <div className="bg-blue-50 border-l-4 border-blue-500 rounded-r-lg p-4">
                <p className="text-sm text-blue-900">
                  <span className="font-semibold">Thank you for sharing your thoughts! </span>
                  Your feedback is invaluable to us and helps us make Qualiz better for everyone. We
                  carefully review every submission to continuously improve your experience.
                </p>
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-6 pt-3 space-y-4">
              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.category}
                  onChange={e => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                  disabled={submitting}
                >
                  <option value="General Feedback">General Feedback</option>
                  <option value="Bug Report">Bug Report</option>
                  <option value="Suggest Feature">Suggest Feature</option>
                  <option value="UX Issue">UX Issue</option>
                </select>
              </div>

              {/* Severity (only for Bug Reports) */}
              {formData.category === 'Bug Report' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Severity <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.severity}
                    onChange={e => setFormData({ ...formData, severity: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                    disabled={submitting}
                  >
                    <option value="Low">Low</option>
                    <option value="Medium">Medium</option>
                    <option value="High">High</option>
                    <option value="Critical">Critical</option>
                  </select>
                </div>
              )}

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={formData.description}
                  onChange={e => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  rows={6}
                  maxLength={2000}
                  placeholder="Please describe your feedback in detail..."
                  required
                  disabled={submitting}
                />
                <div className="text-sm text-gray-500 text-right mt-1">
                  {formData.description.length}/2000
                </div>
              </div>

              {/* Screenshot */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Screenshot (Optional)
                </label>
                {!formData.screenshot ? (
                  <button
                    type="button"
                    onClick={captureScreenshot}
                    disabled={capturing || submitting}
                    className="w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors flex items-center justify-center gap-2 text-gray-700 disabled:opacity-50"
                  >
                    {capturing ? (
                      <>
                        <Loader className="w-5 h-5 animate-spin" />
                        Capturing...
                      </>
                    ) : (
                      <>
                        <Camera className="w-5 h-5" />
                        Capture Screenshot
                      </>
                    )}
                  </button>
                ) : (
                  <div className="relative border border-gray-300 rounded-lg p-2">
                    <img
                      src={formData.screenshotPreview}
                      alt="Screenshot preview"
                      className="w-full rounded"
                    />
                    <button
                      type="button"
                      onClick={removeScreenshot}
                      className="absolute top-4 right-4 bg-red-600 hover:bg-red-700 text-white rounded-full p-2 shadow-lg transition-colors"
                      disabled={submitting}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>

              {/* Context Info */}
              <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-600">
                <p className="font-medium mb-1">Auto-captured context:</p>
                <p>Page: {getCurrentPageName()}</p>
                <p>Role: {user.role}</p>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={handleCloseForm}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting || !formData.description.trim()}
                  className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {submitting ? (
                    <>
                      <Loader className="w-5 h-5 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      Submit Feedback
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};
